/**
 * useEmailThreadHeaderActions — per-entity-type header actions for email
 * threads, mirroring `useMeetingHeaderActions`.
 *
 * Contributes the thread-level reply actions, rendered as normal buttons in
 * the record header's action bar — the top-of-slideout home every other
 * entity's actions use — so replying never requires scrolling. They target
 * the MOST RECENT message, whatever its direction, driving the shared
 * `useEmailReply` behavior: an inbound latest drafts back to its sender;
 * when the viewer spoke last, the draft continues to that message's own
 * recipients (the mail-client convention) instead of skipping back to an
 * older email.
 *
 * When the latest message has OTHER recipients beyond its sender and the
 * viewer, "Reply" (primary recipients only) and "Reply all" (CCs kept) are
 * genuinely different actions, so both are offered — the mail-client
 * pairing. With no other recipients they collapse to one "Reply".
 *
 * For non-thread entities every inner hook receives disabled/undefined args
 * and short-circuits (no timeline query); the returned array is always the
 * same empty reference.
 */

import { CornerUpLeft } from '@untitledui/icons';
import { useMemo } from 'react';

import type { ResponsiveAction } from '@/lib/base/buttons';
import type { CrmObject } from '@/types/crm';

import { useEmailReply } from '../timeline/events/useEmailReply';
import { useThreadTimeline } from '../timeline/useThreadTimeline';

const EMPTY_ACTIONS: ResponsiveAction[] = [];

export function useEmailThreadHeaderActions(
  object: CrmObject | null
): ResponsiveAction[] {
  const isThread = object?.entity_type_name === 'ahoy_email_thread';

  // The shared thread-timeline hook — same query as every other thread
  // surface, disabled entirely for non-thread entities.
  const { messages } = useThreadTimeline({
    entityId: isThread ? object.entity_id : '',
    orgId: isThread ? object.org_id : '',
    enabled: isThread,
  });

  const latestMessage = messages[messages.length - 1];

  const {
    canReply,
    hasOtherRecipients,
    handleReply,
    handleReplySender,
    isCreatingReply,
  } = useEmailReply({
    entityId: isThread ? object.entity_id : undefined,
    replyToEventId: latestMessage?.eventId,
    payload: latestMessage?.payload,
    isOutbound: latestMessage?.isOutbound ?? false,
    // This hook runs on EVERY record surface — without the gate, opening a
    // contact/company/meeting slideout would fire mailbox-status requests
    // for an action that can never render there.
    enabled: isThread,
  });

  return useMemo(() => {
    if (!isThread || !canReply) {
      return EMPTY_ACTIONS;
    }
    // Same icon for both — the UntitledUI set has no double-arrow reply-all
    // glyph, and the inline labels carry the distinction.
    const reply: ResponsiveAction = {
      key: 'thread-reply',
      label: 'Reply',
      icon: CornerUpLeft,
      onClick: () => {
        void handleReplySender();
      },
      isDisabled: isCreatingReply,
      placement: 'inline' as const,
    };
    if (!hasOtherRecipients) {
      return [reply];
    }
    return [
      reply,
      {
        key: 'thread-reply-all',
        label: 'Reply all',
        icon: CornerUpLeft,
        onClick: () => {
          void handleReply();
        },
        isDisabled: isCreatingReply,
        placement: 'inline' as const,
      },
    ];
  }, [
    isThread,
    canReply,
    hasOtherRecipients,
    handleReply,
    handleReplySender,
    isCreatingReply,
  ]);
}
